"use client";
import Link from "next/link";
import { useIsDesktopShell } from "@/hooks/useViewport";
import SummaryPdfTrackRecord from "./SummaryPdfTrackRecord";
import SummaryPdfTestimonials from "./SummaryPdfTestimonials";
import SummaryPdfDescription from "./SummaryPdfDescription";
import Script from "next/script";
import PdfDetailShell from "./PdfDetailShell";
import ProductCarousel from "@/components/ProductCarousel";
import PaymentSection from "@/components/PaymentSection";
import SummaryPdfBundleBanner from "./SummaryPdfBundleBanner";
import { SUMMARY_PDF_2027_1, isSummaryPdfLaunchOpen } from "@/lib/summary-pdf-sales";

export default function UpcomingSummaryPdfClient({ productId, launched }: { productId?: string; launched: boolean }) {
  const isDesktop = useIsDesktopShell();
  const product = SUMMARY_PDF_2027_1.products.find(p => p.id === productId);
  const isBundle = !product;
  const item = product ?? SUMMARY_PDF_2027_1.bundle;
  const products = product ? [product] : SUMMARY_PDF_2027_1.products;
  // 서버 판정에 더해 클라이언트의 공개 설정도 확인한다. 실제 주문은 서버에서 다시 검증한다.
  const canBuy = launched && isSummaryPdfLaunchOpen();

  const leftAside = <div className="space-y-4" data-pdf-panel="left">
    {isBundle ? (
    <nav aria-label="요약 PDF 상품" className="space-y-3 rounded-xl border bg-card p-4">
      <h2 className="text-sm font-semibold">2027년 1회 요약 PDF</h2>
      <Link className="block text-sm text-muted-foreground underline underline-offset-4" href={"/summary-pdf"}>요약 PDF 전체 보기</Link>
      {SUMMARY_PDF_2027_1.products.map(p => <Link key={p.id} href={p.href} className="block rounded-lg border p-3 text-sm hover:bg-muted" aria-current={p.id === productId ? "page" : undefined}>{p.title}<span className="mt-1 block text-muted-foreground">각 {p.price.toLocaleString()}원</span></Link>)}
    </nav>
    ) : (
      <Link href={SUMMARY_PDF_2027_1.products[productId === "27-1-compressed-pdf" ? 1 : 0].href} className="group block break-words rounded-xl border border-blue-200 bg-blue-50 p-4 transition-colors hover:bg-blue-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:border-blue-900 dark:bg-blue-950/40 dark:hover:bg-blue-950/60">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-sm font-bold text-blue-800 dark:text-blue-300">{productId === "27-1-compressed-pdf" ? "💻 실기 코딩 요약 PDF" : "📘 실기 이론 요약 PDF"}</h2>
          <span aria-hidden className="shrink-0 text-lg text-blue-600 transition-transform group-hover:translate-x-0.5 dark:text-blue-400">→</span>
        </div>
        <p className="mt-1 text-xs leading-relaxed text-blue-700 dark:text-blue-200">{productId === "27-1-compressed-pdf" ? "C·Java·Python·SQL 기출 코드 패턴을 정리한 코딩 요약 PDF도 함께 학습하세요!" : "기출 핵심 키워드를 정리한 이론 요약 PDF도 함께 학습하세요!"}</p>
      </Link>
    )}
    <SummaryPdfTrackRecord theory={products.some(p => p.id === "27-1-compressed-pdf")} coding={products.some(p => p.id === "27-1-coding-pdf")} />
    {!isBundle && <SummaryPdfBundleBanner variant={isDesktop ? "compact" : "full"} />}
    <div className="space-y-2 rounded-xl border bg-card p-4 text-sm text-muted-foreground">
      <h2 className="font-semibold text-foreground">구매 전 확인</h2>
      <Link href="/terms" className="inline-block underline underline-offset-4">이용약관 및 환불 안내</Link>
    </div>
  </div>;
  const purchase = <div className="space-y-5" data-pdf-panel="purchase">
    {!canBuy && <p role="status" className="rounded-lg border bg-muted p-4 text-sm">11월 1일 낮 12시부터 판매합니다. 발행 준비가 완료되면 구매할 수 있습니다.</p>}
    <section aria-label="상품 구매" className="space-y-5">
      <h1 className="text-2xl font-bold leading-tight break-keep">{item.title}</h1>
      <h2 id="pdf-price" className="text-xl font-bold">{isBundle ? "두 권 함께 구매" : "PDF 구매"}</h2>
      {isBundle && <dl className="space-y-2 text-sm"><div className="flex justify-between"><dt>합계 정가</dt><dd>17,600원</dd></div><div className="flex justify-between"><dt>묶음 할인 10%</dt><dd>−1,760원</dd></div></dl>}
      <p className="text-3xl font-bold tabular-nums">{item.price.toLocaleString()}원</p>
      {canBuy ? <><Script src={process.env.NEXT_PUBLIC_NICEPAY_JS_SDK_URL} /><PaymentSection price={item.price} productId={item.id} orderIdPrefix="PDF-" nicepayClientId={process.env.NEXT_PUBLIC_NICEPAY_CLIENT_ID!} /></> : <button type="button" disabled className="w-full rounded-lg bg-muted px-5 py-3 font-semibold text-muted-foreground">출시 준비 중</button>}
    </section>

  </div>;
  const imageSets = products.map(p => p.publication?.previews.length ? p.publication.previews : Array.from({ length: p.id === "27-1-coding-pdf" ? 6 : 7 }, (_, i) => ({
    src: p.id === "27-1-coding-pdf" ? `/summary-pdf/26-3-coding/v1-0/${i + 1}.webp` : `/summary-pdf/26-3-compressed/v1-0/8e5b8b63/${i + 1}.webp`,
    alt: `2026년 3회 ${p.id === "27-1-coding-pdf" ? "코딩" : "이론"} PDF 미리보기 ${i + 1}`,
  })));
  const images = Array.from({ length: Math.max(0, ...imageSets.map(set => set.length)) }, (_, i) => imageSets.flatMap(set => set[i] ? [set[i]] : [])).flat();
  const gallery = <section aria-label="상품 이미지" className="mb-10 space-y-3" data-pdf-panel="images">
    {images.length ? <div className="[&>div]:lg:w-full"><ProductCarousel images={images} /></div> : <p className="rounded-xl border bg-muted p-8 text-center">새 PDF 미리보기 이미지 준비 중</p>}
    {!products.every(p => p.publication?.previews.length) && <p className="text-center text-xs text-muted-foreground">2026년 3회 PDF 기준 예시 이미지입니다.</p>}
  </section>;
  const description = <div className="space-y-8" data-pdf-panel="description">
    <SummaryPdfDescription theory={products.some(p => p.id === "27-1-compressed-pdf")} coding={products.some(p => p.id === "27-1-coding-pdf")} />
    <SummaryPdfTestimonials />
    <section className="space-y-3 text-sm text-muted-foreground" aria-labelledby="pdf-delivery">
      <h2 id="pdf-delivery" className="text-lg font-semibold text-foreground">제공 방식과 환불 안내</h2>
      <p>결제 완료 후 구매 목록에 PDF가 표시됩니다. 묶음은 이론·코딩 PDF 각각의 항목에서 열람·다운로드합니다.</p>
      <p>환불은 다운로드 이력과 기존 환불 정책에 따라 확인합니다. 묶음 환불 문의에는 묶음 주문번호를 알려주세요.</p>
      <Link className="inline-block underline underline-offset-4" href="/terms">이용약관 및 환불 안내</Link>
    </section>

  </div>;
  const fallback = <div className="mx-auto w-full max-w-[800px] space-y-8 px-4 py-8">
    {gallery}
    {purchase}
    {leftAside}
    {description}
  </div>;
  if (!isDesktop) return fallback;
  return <PdfDetailShell leftAside={leftAside} cta={purchase} fallback={fallback}>
    {gallery}
    {description}
  </PdfDetailShell>;
}
